class ValueEntry<TValue> {
    constructor(readonly value: TValue,
                readonly createdMillis: number) {
    }
}

/**
 * This is a special version of a cache which allows items to be lazily loaded via
 * a user-defined function. When multiple requests are made for the same resource,
 * only a single request to the user-defined function are called as the promises
 * are cached until they are resolved.
 *
 * expirationDelayMillis: Positive value sets an expiration delay in milliseconds
 *                        Zero or negative means values do not expire
 */
export class PromiseCache<TKey, TValue> {
    private readonly values = new Map<TKey, ValueEntry<TValue>>()
    private readonly valuePromises = new Map<TKey, Promise<TValue>>()

    constructor(private readonly expirationDelayMillis: number = 0) {
    }

    /**
     * Gets the value from the cache from the specified key or retrieves the
     * value using the getter if not found.
     */
    get(key: TKey, getter: (key: TKey) => Promise<TValue>): Promise<TValue> {
        // check if this value is already loaded and not expired
        const value = this.getValue(key)
        if (value !== undefined)
            return Promise.resolve(value)

        // check if retrieval has already been started
        let valuePromise = this.valuePromises.get(key)
        if (!valuePromise) {
            // load and store the new retrieval promise
            valuePromise = getter(key)
                .then(value => {
                    this.set(key, value)
                    return value
                })
            this.valuePromises.set(key, valuePromise)
        }

        return valuePromise
    }

    /**
     * Stores the value in the cache
     */
    set(key: TKey, value: TValue) {
        this.values.set(key, new ValueEntry<TValue>(value, Date.now()))
        this.valuePromises.delete(key)
    }

    /**
     * Deletes the value from the cache
     */
    delete(key: TKey) {
        this.values.delete(key)
        this.valuePromises.delete(key)
    }

    /**
     * Gets the value from the cache if it exists and hasn't expired
     */
    private getValue(key: TKey): TValue|undefined {
        const entry = this.values.get(key)
        if (!entry || this.isExpired(entry))
            return undefined

        return entry.value
    }

    private isExpired(entry: ValueEntry<TValue>): boolean {
        if (this.expirationDelayMillis <= 0)
            return false

        const now = Date.now()
        return now > (entry.createdMillis + this.expirationDelayMillis)
    }
}